/**
 * UserNotesList Component
 *
 * Displays user's notes list with the modern design.
 * For users with admin privileges (operations_support, practice_administrator, etc.),
 * shows "My Notes" by default with an option to view "All Clinic Notes".
 * Remembers the last selected tab in localStorage.
 */

import React, { useState, useEffect } from 'react';
import { Box, Tab, Tabs } from '@mui/material';

import { useUserState, useGroupUserRoles } from '@features/user';
import { NotesListPage, NotesListContent } from '@features/notes';
import { hasAdminPrivileges } from '@utils/roleUtils';

const NOTES_TAB_STORAGE_KEY = 'adminNotesTabPreference';

export const UserNotesList: React.FC = () => {
  const groupUserRoles = useGroupUserRoles();
  const isAdmin = hasAdminPrivileges(groupUserRoles);
  const userState = useUserState();
  const groupId = userState?.group_ids?.[0];

  // Get initial view mode from localStorage or default to 'my-notes' for admin
  const getInitialViewMode = (): string => {
    if (!isAdmin) return 'list';
    const saved = localStorage.getItem(NOTES_TAB_STORAGE_KEY);
    return saved === 'group' || saved === 'my-notes' ? saved : 'my-notes';
  };

  const [viewMode, setViewMode] = useState<string>(getInitialViewMode);

  // Save to localStorage when view mode changes (only for admin users)
  useEffect(() => {
    if (isAdmin && (viewMode === 'group' || viewMode === 'my-notes')) {
      localStorage.setItem(NOTES_TAB_STORAGE_KEY, viewMode);
    }
  }, [viewMode, isAdmin]);

  // If not admin, render the notes list directly
  if (!isAdmin) {
    return <NotesListPage title="Notes" />;
  }

  // With admin privileges, show "My Notes" by default with "All Clinic Notes" option
  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto', p: { xs: 2, sm: 3 } }}>
      <Tabs
        value={viewMode}
        onChange={(_, newValue) => setViewMode(newValue)}
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          mb: 3,
        }}
      >
        <Tab label="All Clinic Notes" value="group" />
        <Tab label="My Notes" value="my-notes" />
      </Tabs>

      {viewMode === 'group' && (
        <NotesListContent
          groupId={groupId}
          title="All Clinic Notes"
          showIssuingUser={true}
          enableDateFilter={false}
        />
      )}

      {viewMode === 'my-notes' && <NotesListContent title="My Notes" />}
    </Box>
  );
};
